import { useEffect } from 'react';

/**
 * A custom hook for modifying document metadata for SEO
 * 
 * @param {Object} options - SEO options
 * @param {string} options.title - Page title
 * @param {string} options.description - Page description
 * @param {string} options.keywords - Keywords for the page (comma-separated)
 * @param {string} options.canonical - Canonical URL
 * @param {Object} options.openGraph - Open Graph metadata
 * @param {Object} options.twitter - Twitter Card metadata
 * @param {Object} options.jsonLd - JSON-LD structured data
 */
const useSEO = ({
  title = "GLabs Automator - Bulk Generate AI Content with Google Labs Tools",
  description = "Automate your AI content creation workflow with GLabs Automator. Generate images, text, music & videos in bulk using Google Labs' ImageFX, Whisk, MusicFX, and Flow (VEO3).",
  keywords = "GLabs Automator, Google Labs Automation, ImageFX, MusicFX, Whisk, Flow, VEO3, AI content generation, bulk AI generation",
  canonical = "https://glabsautomator.com",
  openGraph = {},
  twitter = {},
  jsonLd = null
}) => {
  useEffect(() => {
    // Update document title
    document.title = title;
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', description);
    
    // Update meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', keywords);
    
    // Update canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonical);
    
    // Update Open Graph metadata
    const ogDefaults = {
      title: title,
      description: description,
      url: canonical,
      type: 'website',
      image: '/src/assets/header/hero.png'
    };
    
    const ogData = { ...ogDefaults, ...openGraph };
    
    Object.entries(ogData).forEach(([key, value]) => {
      let ogTag = document.querySelector(`meta[property="og:${key}"]`);
      if (!ogTag) {
        ogTag = document.createElement('meta');
        ogTag.setAttribute('property', `og:${key}`);
        document.head.appendChild(ogTag);
      }
      ogTag.setAttribute('content', value);
    });
    
    // Update Twitter Card metadata
    const twitterDefaults = {
      card: 'summary_large_image',
      title: title,
      description: description,
      image: '/src/assets/header/hero.png'
    };
    
    const twitterData = { ...twitterDefaults, ...twitter };
    
    Object.entries(twitterData).forEach(([key, value]) => {
      let twitterTag = document.querySelector(`meta[name="twitter:${key}"]`);
      if (!twitterTag) {
        twitterTag = document.createElement('meta');
        twitterTag.setAttribute('name', `twitter:${key}`);
        document.head.appendChild(twitterTag);
      }
      twitterTag.setAttribute('content', value);
    });
    
    // Add JSON-LD structured data
    if (jsonLd) {
      let jsonLdScript = document.querySelector('script[type="application/ld+json"]');
      if (!jsonLdScript) {
        jsonLdScript = document.createElement('script');
        jsonLdScript.setAttribute('type', 'application/ld+json');
        document.head.appendChild(jsonLdScript);
      }
      jsonLdScript.textContent = JSON.stringify(jsonLd);
    }
    
    // Clean up function
    return () => {
      // Optional: Clean up any dynamic tags if needed when component unmounts
    };
  }, [title, description, keywords, canonical, openGraph, twitter, jsonLd]);
};

export default useSEO;
